/**
 * Step 9 — Generate & Attach (03-17). Preview / Save-to-Documents / e-sign are
 * provided by the always-present `FormEditorActions` below the wizard; this panel
 * adds the completion actions: a link to add a case note (Tier 2.1) and
 * **Complete workflow** (marks the `ResolutionWorkflow` COMPLETED + writes a Case
 * system-note; it then *offers* — never silently applies — a case-status change
 * to Pending IRS).
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import type { FormSubmission } from "@/features/forms/types";
import { completeWorkflow, markCasePendingIrs } from "@/features/workflows/api";
import type { ResolutionWorkflow } from "@/features/workflows/types";

interface Props {
  clientId: string;
  caseId: string;
  submission: FormSubmission;
  workflow: ResolutionWorkflow;
  onCompleted: (workflow: ResolutionWorkflow) => void;
}

export function ExciseGenerateStep({ clientId, caseId, submission, workflow, onCompleted }: Props) {
  const [completing, setCompleting] = useState(false);
  const [prompt, setPrompt] = useState(false);
  const done = workflow.status === "COMPLETED";

  async function handleComplete() {
    setCompleting(true);
    try {
      const updated = await completeWorkflow(caseId, workflow.id);
      onCompleted(updated);
      toast.success("Workflow completed — a note was added to the case.");
      setPrompt(true);
    } catch {
      toast.error("Couldn't complete the workflow.");
    } finally {
      setCompleting(false);
    }
  }

  async function handleMarkPending() {
    try {
      await markCasePendingIrs(caseId);
      toast.success("Case marked Pending IRS.");
    } catch {
      toast.error("Couldn't update the case status.");
    } finally {
      setPrompt(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <Card className="flex flex-col gap-3 p-5">
        <h3 className="font-semibold">Generate Form 4180</h3>
        <p className="text-muted-foreground text-sm">
          Use <span className="font-medium">Preview PDF</span> to review the filled
          Form 4180, then <span className="font-medium">Save to Documents</span> to
          store it in the client's Forms folder and attach it to this case.
        </p>
        {submission.generatedDocument && (
          <p className="text-sm">
            Saved to Documents:{" "}
            <span className="font-medium">{submission.generatedDocument.name}</span>
          </p>
        )}
      </Card>

      <Card className="flex flex-wrap items-center gap-3 p-5">
        <div className="mr-auto">
          <h3 className="font-semibold">Finish up</h3>
          <p className="text-muted-foreground text-sm">
            {done ? "This workflow is complete." : "Add a note or complete the workflow."}
          </p>
        </div>
        <Button asChild variant="outline">
          <Link to={`/client/${clientId}/cases/${caseId}`}>Add a case note</Link>
        </Button>
        <Button onClick={handleComplete} disabled={completing || done}>
          {done ? "Completed" : completing ? "Completing…" : "Complete workflow"}
        </Button>
      </Card>

      <AlertDialog open={prompt} onOpenChange={setPrompt}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark this case Pending IRS?</AlertDialogTitle>
            <AlertDialogDescription>
              The workflow is complete and a note was added to the case. Optionally
              move the case to Pending IRS — this does not happen automatically.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Not now</AlertDialogCancel>
            <AlertDialogAction onClick={handleMarkPending}>Mark Pending IRS</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
